/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import barcodeScannerStyles from "@eisinc/App/Components/BarcodeScanner/BarcodeScanner.style";
import ScannerModalContent from "@eisinc/App/Components/BarcodeScanner/ScannerModalContent";
import QrCode from "@eisinc/XDL/Icons/QrCode";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import logger from "@insite/client-framework/Logger";
import siteMessage from "@insite/client-framework/SiteMessage";
import loadProductInfoList from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/LoadProductInfoList";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import Modal from "@insite/mobius/Modal";

import Clickable from "@insite/mobius/Clickable";
import Icon, { IconMemo } from "@insite/mobius/Icon/Icon";

import { CustomAppState } from "@eisinc/App/Store/Reducers";
import Select from "@eisinc/MobiusOverrides/Select";
import ProductPrice from "@eisinc/Overrides/Components/ProductPrice";
import ProductQuantityOrdered from "@eisinc/Overrides/Components/ProductQuantityOrdered";
import ProductUnitOfMeasureSelect from "@eisinc/Overrides/Components/ProductUnitOfMeasureSelect";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import {
    getProductInfoFromList,
    getProductsForProductInfoList,
} from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import { Button, Typography } from "@insite/mobius/index";
import Link from "@insite/mobius/Link";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { Html5Qrcode } from "html5-qrcode";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: CustomAppState, props: OwnProps) => {
    const products = getProductsForProductInfoList(state, props.id);
    return {
        productInfo: getProductInfoFromList(state, props.id, products?.[0]?.id as string),
        cartId: getCurrentCartState(state).value?.id,
        globalMessageSate: state.xHeader.globalMessage,
    };
};

const mapDispatchToProps = {
    addToCart: makeHandlerChainAwaitable<Parameters<typeof addToCart>[0], CartLineModel>(addToCart),
    removeCartLine,
    updateCartLine,
    updateProductInfo,
};

const styles = barcodeScannerStyles;

type QRCodeResult = {
    erpNumber: string;
    qty: number;
    uom: string;
};

type OwnProps = {
    id: string;
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

let initializing = false;

const BarcodeScanner: React.FC<Props> = ({
    id,
    addToCart,
    removeCartLine,
    cartId,
    globalMessageSate,
    updateCartLine,
    updateProductInfo,
    productInfo,
}) => {
    if (
        typeof window === "undefined"
        // !navigator.mediaDevices ||
        // typeof navigator.mediaDevices.getUserMedia !== "function"
    ) {
        return null;
    }

    const dispatch = useAppDispatch();
    const [isOpen, setIsOpen] = useState(false);
    const [removeClicked, setRemoveClicked] = React.useState(false);
    const [isUpdateDisabled, setIsUpdateDisabled] = React.useState(false);
    const [isShowUpdateSuccessMessage, setShowUpdateSuccessMessage] = React.useState(false);
    const [cartLine, setCartLine] = useState<CartLineModel>();
    const [searchedProductInfo, setSearchedProductInfo] = useState<QRCodeResult>();
    const [searchedProduct, setSearchedProduct] = useState<ProductModel>();
    const [html5QrCode, setHtml5QrCode] = useState<Html5Qrcode>();
    const [productContext, setProductContext] = useState<ProductContextModel>();
    const [newQty, setNewQty] = useState<number>();
    const [newUOM, setNewUOM] = useState<string>();
    const [erpNum, setErpNum] = useState<string>();
    const [removedItem, setRemovedItem] = useState(false);
    const [showNotAvailable, setShowNotAvailable] = useState(false);
    const [buttonStatus, setButtonStatus] = useState<string>("Update");

    let localCartLine = {} as any;

    let isAddingToCart = false;
    const elementID = "barcode-scanner-live-stream";

    const barcodeButtonClickHandler = () => {
        if (!initializing) {
            initializing = true;
            setIsOpen(true);
            localStorage.removeItem("cartLine");
        }
    };

    const closeModal = () => {
        if (initializing) {
            // check in case the user didn't allow using the camera
            if ((html5QrCode as any).borderShaders) {
                html5QrCode?.stop();
            }
            initializing = false;
        }
        setIsOpen(false);
        clearScannerState();
        setShowNotAvailable(false);
    };

    const clearScannerState = () => {
        setSearchedProductInfo({} as QRCodeResult);
        setSearchedProduct(undefined);
        localStorage.removeItem("cartLine");
        setCartLine({} as CartLineModel);
        setRemoveClicked(false);
        setRemovedItem(false);
    };

    useEffect(() => {
        if (
            window.location.search &&
            window.location.search.includes("erp") &&
            window.location.search.includes("qty") &&
            window.location.search.includes("uom") &&
            !initializing
        ) {
            initializing = true;
            setIsOpen(true);
            handleResultChange(window.location.href);
        }
    }, []);

    const handleResultChange = (value: string) => {
        const url = new URL(value);
        const erpNumber: string = url.searchParams.get("erp") || "";
        const qty: number = Number(url.searchParams.get("qty")) || 0;
        const uom: string = url.searchParams.get("uom") || "";

        setShowNotAvailable(false);

        setErpNum(erpNumber);
        setNewUOM(undefined);
        setNewQty(undefined);
        setButtonStatus("Update");
        // get the latest added product from the localStorage
        localCartLine = localStorage.getItem("cartLine");
        if (localCartLine) {
            localCartLine = JSON.parse(localCartLine);
            setCartLine(localCartLine);
        }

        // prevent adding the same product multiple time to prevent the infinite loop
        if (erpNumber && qty && uom && !isAddingToCart && erpNumber !== localCartLine?.erpNumber) {
            setRemovedItem(false);
            isAddingToCart = true;
            setIsUpdateDisabled(true);
            setShowNotAvailable(false);
            dispatch(
                loadProductInfoList({
                    getProductCollectionParameter: { productNumbers: [erpNumber] },
                    id,
                    async onComplete(result: any) {
                        if (result?.products.length === 0) {
                            isAddingToCart = false;
                            setIsUpdateDisabled(false);
                            setShowNotAvailable(true);
                            localStorage.removeItem("cartLine");
                            return;
                        }
                        setSearchedProduct(result?.products?.[0]);
                        setSearchedProductInfo({
                            erpNumber,
                            qty,
                            uom,
                        } as QRCodeResult);
                        if (result?.products?.[0]) {
                            const cartLineResult = await addToCart({
                                productId: result?.products?.[0]?.id,
                                qtyOrdered: qty,
                                unitOfMeasure: uom,
                            });

                            isAddingToCart = false;
                            setIsUpdateDisabled(false);
                            localStorage.setItem("cartLine", JSON.stringify(cartLineResult));
                            setCartLine(JSON.parse(JSON.stringify(cartLineResult)));
                            setRemoveClicked(false);
                            setIsUpdateDisabled(false);
                            setShowNotAvailable(false);
                        } else {
                            isAddingToCart = false;
                            setIsUpdateDisabled(false);
                        }
                    },
                }),
            );
        }
    };

    const handleError = (errorMessage: string) => {
        logger.error(errorMessage);
    };

    const initHtml5QrCode = () => {
        const newHtml5Q = new Html5Qrcode(elementID);
        setHtml5QrCode(newHtml5Q);

        return new Promise((resolve, reject) => {
            resolve(newHtml5Q);
        }) as Promise<Html5Qrcode>;
    };

    const removeCartLineClickHandler = () => {
        setRemoveClicked(true);

        removeCartLine({
            cartLineId: cartLine?.id,
            cartId,
            onComplete() {
                setRemovedItem(true);
                setSearchedProduct(undefined);
                setProductContext(undefined);
                setNewQty(undefined);
                setNewUOM(undefined);
            },
        } as any);
    };

    const uomChangeHandler = (uom: string) => {
        setNewUOM(uom);
        setButtonStatus("ADD");
        updateProductInfo({
            id,
            productId: searchedProduct?.id as string,
            unitOfMeasure: uom,
        });
    };

    useEffect(() => {
        if (productInfo && searchedProduct) {
            const currentProductInfo: ProductInfo = {
                ...productInfo,
                qtyOrdered: newQty || (searchedProductInfo?.qty as number),
                unitOfMeasure: newUOM || (searchedProductInfo?.uom as string),
            };

            setProductContext({
                product: searchedProduct,
                productInfo: currentProductInfo,
                onUnitOfMeasureChanged: uomChangeHandler,
                onQtyOrderedChanged: qtyOrderedChangeHandler,
            });
        }
    }, [productInfo, newUOM]);

    const qtyOrderedChangeHandler = (qtyOrdered: number) => {
        setNewQty(qtyOrdered);
        setProductContext((prev: any) => {
            return {
                ...prev,
                productInfo: {
                    ...prev?.productInfo,
                    qtyOrdered,
                },
            };
        });
    };

    const updateCart = async () => {
        if (newUOM && newUOM !== cartLine?.unitOfMeasure) {
            const updatedCart = await addToCart({
                productId: productContext?.productInfo.productId as string,
                qtyOrdered: newQty || (searchedProductInfo?.qty as number),
                unitOfMeasure: newUOM || (searchedProductInfo?.uom as string),
            });
            setShowUpdateSuccessMessage(false);
            setCartLine(updatedCart);
            setNewQty(undefined);
            setButtonStatus("Update");
        } else {
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    qtyOrdered: newQty || (searchedProductInfo?.qty as number),
                    unitOfMeasure: newUOM ?? (searchedProductInfo?.uom as string),
                    properties: { ...cartLine?.properties, oldQty: cartLine?.qtyOrdered?.toString() || "1" },
                },
                cartId,
            } as any);
            setShowUpdateSuccessMessage(true);
            setNewQty(undefined);
            setButtonStatus("Update");
        }
    };

    return (
        <StyledWrapper>
            <Clickable {...styles.barcodeButton} onClick={barcodeButtonClickHandler}>
                <Icon {...styles.barcodeIcon} src={QrCode} />
                <VisuallyHidden>{translate("Scan QR Code")}</VisuallyHidden>
            </Clickable>

            <Modal
                {...styles.mobileBarcodeModal}
                isOpen={isOpen}
                closeOnEsc={false}
                handleClose={closeModal}
                headline={translate("Scan QR Code")}
                className={globalMessageSate ? "qr-scanner global-site-message-found" : "qr-scanner"}
            >
                <ScannerModalContent
                    onChange={handleResultChange}
                    onError={handleError}
                    isOpen={isOpen}
                    initHtml5QrCode={initHtml5QrCode}
                />

                <StyledWrapper {...styles.modalFooter}>
                    {!searchedProduct && !removedItem && !showNotAvailable && (
                        <Typography {...styles.placeholderMessage}>
                            {siteMessage("custom_qr_placeholder_msg")}
                        </Typography>
                    )}
                    {removedItem && !isUpdateDisabled && (
                        <Typography {...styles.sucessAtcMsg}>
                            {translate("Item successfully removed from your cart!")}
                        </Typography>
                    )}
                    {showNotAvailable && (
                        <StyledWrapper {...styles.resultSection}>
                            <Typography {...styles.notFoundProductMsg}>
                                {erpNum} {translate("could not be found")}
                            </Typography>
                            <Typography {...styles.notFoundProductMsg}>
                                {siteMessage("custom_scan_qr_code_error_msg")}
                            </Typography>
                            <Link
                                onClick={() => {
                                    closeModal();
                                }}
                                {...styles.learnMore}
                                href={`${siteMessage("custom_qr_learn_more_link")}`}
                            >
                                {translate("Learn More")}
                            </Link>
                        </StyledWrapper>
                    )}
                    {searchedProduct && productInfo && (
                        <StyledWrapper {...styles.resultSection}>
                            {cartLine?.id && (
                                <StyledWrapper>
                                    {!isShowUpdateSuccessMessage && (
                                        <Typography className="add-margin" {...styles.sucessAtcMsg}>
                                            {productInfo.qtyOrdered} {productInfo.unitOfMeasure}{" "}
                                            {siteMessage("custom_qr_code_atc_msg")}
                                        </Typography>
                                    )}
                                    {isShowUpdateSuccessMessage && (
                                        <Typography className="add-margin" {...styles.sucessAtcMsg}>
                                            {siteMessage("custom_qr_code_update_msg")}
                                        </Typography>
                                    )}
                                </StyledWrapper>
                            )}
                            <StyledWrapper {...styles.productDetails}>
                                <Typography {...styles.productTitleText}>{searchedProduct.productTitle}</Typography>
                                <StyledWrapper>
                                    {searchedProduct.customerProductNumber && (
                                        <Typography {...styles.valueText} as="p">
                                            <Typography {...styles.labelText}>
                                                {siteMessage("Custom_Customer_#", "Customer #:")}
                                            </Typography>
                                            {searchedProduct.customerProductNumber}
                                        </Typography>
                                    )}
                                    {searchedProduct.manufacturerItem && (
                                        <Typography {...styles.valueText} as="p">
                                            <Typography {...styles.labelText}>
                                                {siteMessage("Custom_MFR_#", "MFR #:")}
                                            </Typography>
                                            {searchedProduct.manufacturerItem}
                                        </Typography>
                                    )}
                                    {searchedProduct.productNumber && (
                                        <Typography
                                            {...styles.valueText}
                                            as="p"
                                            data-test-selector="ProductDetailsPartNumber"
                                        >
                                            <Typography {...styles.labelText}>
                                                {siteMessage("Custom_Part_#", "EIS Part #:")}
                                            </Typography>
                                            {searchedProduct.productNumber}
                                        </Typography>
                                    )}
                                </StyledWrapper>
                                {productContext && (
                                    <ProductContext.Provider value={productContext}>
                                        <StyledWrapper {...styles.productPrice}>
                                            <ProductPrice
                                                showLabel={false}
                                                showPack={false}
                                                product={productContext}
                                                showInvalidPriceMessage={true}
                                                showUnitPriceDisplay={true}
                                                showUnitOfMeasure={false}
                                            />
                                        </StyledWrapper>
                                        <StyledWrapper {...styles.cartActionsContainer}>
                                            <StyledWrapper {...styles.leftSide}>
                                                <ProductQuantityOrdered labelOverride="" extendedStyles={styles.qty} />
                                            </StyledWrapper>
                                            <StyledWrapper {...styles.rightSide}>
                                                {productContext.product.unitOfMeasures &&
                                                    productContext.product.unitOfMeasures.length > 1 && (
                                                        <StyledWrapper {...styles.uom}>
                                                            <ProductUnitOfMeasureSelect
                                                                labelOverride=" "
                                                                extendedStyles={styles.unitOfMeasureSelect}
                                                            />
                                                        </StyledWrapper>
                                                    )}
                                                {productContext.product.unitOfMeasures &&
                                                    productContext.product.unitOfMeasures.length === 1 && (
                                                        <StyledWrapper {...styles.uom}>
                                                            <Select
                                                                data-test-selector="product_unitOfMeasureSelect"
                                                                {...styles.unitOfMeasureSingleUOM}
                                                            >
                                                                <option>
                                                                    {productContext?.product?.unitOfMeasures?.[0]
                                                                        .unitOfMeasureDisplay
                                                                        ? productContext?.product?.unitOfMeasures?.[0]
                                                                              .unitOfMeasureDisplay
                                                                        : ""}
                                                                </option>
                                                            </Select>
                                                        </StyledWrapper>
                                                    )}
                                            </StyledWrapper>
                                        </StyledWrapper>
                                    </ProductContext.Provider>
                                )}

                                {productContext && (
                                    <Button
                                        {...styles.updateButton}
                                        onClick={updateCart}
                                        disabled={buttonStatus === "Update" && !newQty}
                                    >
                                        {translate(`${buttonStatus === "Update" ? "Update" : "ADD"}`)}
                                    </Button>
                                )}

                                {isUpdateDisabled && (
                                    <StyledWrapper {...styles.addingToCartWrapper}>
                                        <Typography {...styles.removeCartLineText}>
                                            {translate("Adding to cart ...")}
                                        </Typography>
                                    </StyledWrapper>
                                )}

                                {!isUpdateDisabled && cartLine?.id && !removeClicked && (
                                    <StyledWrapper>
                                        <Clickable onClick={removeCartLineClickHandler} {...styles.removeCartLineBtn}>
                                            <IconMemo {...styles.removeCartLineIcon} />
                                            <Typography {...styles.removeCartLineText}>
                                                {translate("Remove From Cart")}
                                            </Typography>
                                        </Clickable>
                                    </StyledWrapper>
                                )}
                            </StyledWrapper>
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            </Modal>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(BarcodeScanner);
